/**
 * CookieConsent — GDPR cookie consent banner.
 * Controls whether Google Analytics 4 (Firebase Analytics) collects data.
 * Consent choice stored in localStorage('sendly-cookie-consent'). With blocked
 * storage the banner shows on every visit and the choice holds for the page only.
 * A static import of App (every boot renders it — a chunk of its own only put a
 * request on the boot's critical path).
 */
import React, { useState, useEffect, useCallback } from "react";
import { F } from "../constants";
import { ViewportLayer, GreenBtn } from "./ui";
import { enableAnalytics } from "../firebase";
import { useTranslation } from "../i18n";
import { localStore } from "../utils/safeStorage";

const STORAGE_KEY = "sendly-cookie-consent";

export function CookieConsent() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStore.get(STORAGE_KEY);
    if (stored === "accepted") {
      enableAnalytics();
    } else if (!stored) {
      setVisible(true);
    }
    // "declined" → do nothing, stay hidden
  }, []);

  const handleAccept = useCallback(() => {
    localStore.set(STORAGE_KEY, "accepted");
    enableAnalytics();
    setVisible(false);
  }, []);

  const handleDecline = useCallback(() => {
    localStore.set(STORAGE_KEY, "declined");
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <ViewportLayer anchor="bottom" zIndex={9999} padding={0}>
      <div role="dialog" aria-label={t('cookie.message')} style={{
        pointerEvents: "auto",
        width: "100%",
        background: "rgba(0,0,0,0.92)", backdropFilter: "blur(10px)",
        padding: "16px 20px", display: "flex", alignItems: "center",
        justifyContent: "center", gap: 16, flexWrap: "wrap",
      }}>
        <div style={{ color: "#fff", fontSize: 13, fontFamily: F, maxWidth: 600, lineHeight: 1.5 }}>
          🍪 {t('cookie.message')}
          {" "}
          <a href="/legal/privacy" target="_blank" rel="noopener noreferrer"
            style={{ color: "#fff", fontWeight: 600, textDecoration: "underline", opacity: 0.9 }}>
            {t('cookie.details')}
          </a>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button onClick={handleDecline} style={{
            padding: "8px 18px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.3)",
            background: "transparent", color: "#fff", fontSize: 13, fontWeight: 600,
            cursor: "pointer", fontFamily: F,
          }}>{t('cookie.decline')}</button>
          <GreenBtn small onClick={handleAccept}>{t('cookie.accept')}</GreenBtn>
        </div>
      </div>
    </ViewportLayer>
  );
}
